import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { Modal } from '../components/parts';
import { GoogleGlyph } from '../components/Icon';
import { useI18n } from '../i18n';
import type { Lang } from '../i18n';
import {
  isHapticsEnabled,
  isMusicEnabled,
  isSoundEnabled,
  setHapticsEnabled,
  setMusicEnabled,
  setSoundEnabled,
} from '../feedback';
import {
  NAME_COLORS,
  colorForId,
  isUnlocked,
  savedNameColor,
  unlockHint,
  type NameColorId,
  type UnlockStats,
} from '../game/cosmetics';
import { equipNameColor, fetchWallet } from '../game/shop';
import { fetchProfileStats } from '../game/profile';
import { ProfileContent } from '../components/Profile';
import { disablePush, enablePush, isPushSupported, pushEnabled } from '../push';

function ToggleRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button className="toggle-row" onClick={() => onChange(!value)}>
      <span>{label}</span>
      <span className={'switch' + (value ? ' on' : '')}><span className="knob" /></span>
    </button>
  );
}

export function Settings({
  user,
  handle,
  onSave,
  onLinkGoogle,
  linkBusy,
  linkError,
  onSignOut,
  onClose,
}: {
  user: User | null;
  handle: string;
  onSave: (handle: string) => void;
  onLinkGoogle: () => void;
  linkBusy: boolean;
  linkError: string | null;
  onSignOut: () => void;
  onClose: () => void;
}) {
  const { t, lang, setLang } = useI18n();
  // One modal, two tabs: the player's stats card and the actual settings form.
  const [tab, setTab] = useState<'profile' | 'settings'>('profile');
  const [name, setName] = useState(handle);
  const [sound, setSound] = useState(isSoundEnabled());
  const [musicOn, setMusicOn] = useState(isMusicEnabled());
  const [haptics, setHaptics] = useState(isHapticsEnabled());
  const [color, setColor] = useState<NameColorId>(savedNameColor);
  const [stats, setStats] = useState<UnlockStats>({ games: 0, wins: 0, mvp: 0 });
  const [push, setPush] = useState(pushEnabled());
  const [pushSupported, setPushSupported] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    isPushSupported().then(setPushSupported);
  }, []);

  // Lifetime stats decide which nick colours are unlocked (colours are earned,
  // never bought).
  useEffect(() => {
    if (!user?.uid) return;
    let alive = true;
    fetchProfileStats(user.uid)
      .then((s) => alive && setStats({ games: s.games, wins: s.wins, mvp: s.mvp }))
      .catch(() => {});
    // fetchWallet reconciles this browser's local cache with the wallet doc,
    // so re-read it to reflect a colour equipped from another browser/device.
    fetchWallet(user.uid)
      .then(() => alive && setColor(savedNameColor()))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user?.uid]);

  const isGuest = user?.isAnonymous ?? true;
  const provider = user?.providerData[0]?.providerId;
  const providerLabel =
    provider === 'apple.com' ? 'Apple' : provider === 'google.com' ? 'Google' : 'account';

  function save() {
    onSave(name.trim().slice(0, 24) || 'Runner');
    onClose();
  }

  const langs: Lang[] = ['en', 'uk'];

  return (
    <Modal onClose={onClose}>
      <div className="col settings-panel" style={{ gap: 18 }}>
        <div className="row between">
          <h2 className="h2">{tab === 'profile' ? t.profile.title : t.settings.title}</h2>
          <button className="link" onClick={onClose}>{t.settings.close}</button>
        </div>

        <div className="segmented">
          <button
            className={'segmented-btn' + (tab === 'profile' ? ' active' : '')}
            onClick={() => setTab('profile')}
          >
            ◈ {t.home.profile}
          </button>
          <button
            className={'segmented-btn' + (tab === 'settings' ? ' active' : '')}
            onClick={() => setTab('settings')}
          >
            ⚙ {t.home.settings}
          </button>
        </div>

        {tab === 'profile' && user?.uid && <ProfileContent uid={user.uid} name={handle} />}

        {tab === 'settings' && (<>
        <div className="col" style={{ gap: 6 }}>
          <span className="field-label">{t.settings.handle}</span>
          <input
            className="input"
            maxLength={24}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Runner"
            style={{ color: colorForId(color), fontWeight: 700 }}
          />
        </div>

        <div className="col" style={{ gap: 6 }}>
          <span className="field-label">{t.settings.nameColor}</span>
          <div className="swatch-row">
            {NAME_COLORS.map((c) => {
              const unlocked = isUnlocked(c, stats);
              const hint = unlockHint(c);
              return (
                <button
                  key={c.id}
                  className={
                    'swatch' + (color === c.id ? ' active' : '') + (unlocked ? '' : ' locked')
                  }
                  style={{ ['--sw' as string]: c.color }}
                  disabled={!unlocked}
                  title={unlocked ? c.id : `${t.settings.locked} · ${hint}`}
                  onClick={() => {
                    setColor(c.id);
                    equipNameColor(c.id).catch(() => {});
                  }}
                >
                  {!unlocked && <span className="swatch-lock">{hint}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="col" style={{ gap: 6 }}>
          <span className="field-label">{t.settings.language}</span>
          <div className="segmented">
            {langs.map((l) => (
              <button
                key={l}
                className={'segmented-btn' + (lang === l ? ' active' : '')}
                onClick={() => setLang(l)}
              >
                {t.langName[l]}
              </button>
            ))}
          </div>
        </div>

        <div className="col" style={{ gap: 4 }}>
          <ToggleRow
            label={t.settings.sound}
            value={sound}
            onChange={(v) => { setSoundEnabled(v); setSound(v); }}
          />
          <ToggleRow
            label={t.settings.music}
            value={musicOn}
            onChange={(v) => { setMusicEnabled(v); setMusicOn(v); }}
          />
          <ToggleRow
            label={t.settings.haptics}
            value={haptics}
            onChange={(v) => { setHapticsEnabled(v); setHaptics(v); }}
          />
          {pushSupported && (
            <ToggleRow
              label={pushBusy ? t.settings.notifyBusy : t.settings.notify}
              value={push}
              onChange={async (v) => {
                if (pushBusy) return;
                if (v) {
                  setPushBusy(true);
                  const ok = await enablePush();
                  setPush(ok);
                  setPushBusy(false);
                } else {
                  disablePush();
                  setPush(false);
                }
              }}
            />
          )}
        </div>

        <div className="col" style={{ gap: 6 }}>
          <span className="field-label">{t.settings.account}</span>
          {isGuest ? (
            <div className="panel col" style={{ padding: 12, gap: 10 }}>
              <div className="col" style={{ gap: 2 }}>
                <span style={{ fontWeight: 700 }}>{t.settings.guest}</span>
                <span className="mono-dim" style={{ fontSize: 11 }}>{t.auth.keep}</span>
              </div>
              <button
                className="btn cyan block row"
                style={{ justifyContent: 'center', gap: 10 }}
                disabled={linkBusy}
                onClick={onLinkGoogle}
              >
                {!linkBusy && <GoogleGlyph />}
                {linkBusy ? t.auth.linking : t.auth.google}
              </button>
              {linkError && <span className="mono-dim" style={{ fontSize: 11 }}>{linkError}</span>}
            </div>
          ) : (
            <div className="panel row between" style={{ padding: 12 }}>
              <div className="col" style={{ gap: 2 }}>
                <span style={{ fontWeight: 700 }}>{user?.displayName ?? user?.email ?? '—'}</span>
                <span className="mono-dim" style={{ fontSize: 11 }}>
                  {t.settings.signedInWith(providerLabel)}
                </span>
              </div>
              <button className="btn small ghost" onClick={onSignOut}>{t.settings.signOut}</button>
            </div>
          )}
        </div>

        <button className="btn block cyan" onClick={save}>{t.settings.save}</button>

        <div className="mono-dim" style={{ fontSize: 11, textAlign: 'center' }}>
          {t.settings.about}
        </div>
        </>)}
      </div>
    </Modal>
  );
}
